import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Menu, Settings, User, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/lib/auth-store';
import { getActiveRole } from '@/lib/roles';
import { cn } from '@/lib/utils';
import { NotificationBell } from './NotificationBell';

/**
 * Top nav for authenticated pages. Role-aware — instructors and admins see the
 * Workspace + Org Admin links; everyone sees Dashboard. The user menu shows
 * the active role + a sign-out action.
 */
export function AppHeader() {
  const { state, signOut } = useAuth();
  const navigate = useNavigate();

  if (state.status !== 'authenticated') return null;

  const profile = state.profile;
  const activeRole = getActiveRole(profile.roles);
  const initials = (profile.displayName ?? profile.fullName)
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const showWorkspace = activeRole === 'instructor' || activeRole === 'teaching_assistant';
  const showOrgAdmin =
    activeRole === 'admin' || activeRole === 'department_admin' || activeRole === 'super_admin';

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
      <div className="container flex h-14 items-center justify-between gap-4">
        {/*
          Top-of-page logo: the brand PNG (mascot + EduBorg wordmark in one image).
          The bouncing robot mascot stays at the bottom in the Companion shell.
          Per Dr. Cato 2026-04-29 — keep visual identity consistent at the top,
          mascot animates only at the bottom-right corner.
        */}
        <Link to="/dashboard" className="flex items-center" aria-label="EduBorg home">
          <img
            src="/brand/eduborg-wordmark.png"
            alt="EduBorg"
            width={140}
            height={36}
            className="h-9 w-auto"
          />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <HeaderLink to="/dashboard">Dashboard</HeaderLink>
          <HeaderLink to="/messages">Messages</HeaderLink>
          {showWorkspace && <HeaderLink to="/workspace">Workspace</HeaderLink>}
          {showOrgAdmin && <HeaderLink to="/org-admin">Org Admin</HeaderLink>}
        </nav>

        <div className="flex items-center gap-2">
          <NotificationBell />
          <UserMenu
            name={profile.displayName ?? profile.fullName}
            activeRole={activeRole}
            initials={initials}
            onSignOut={async () => {
              await signOut();
              navigate('/');
            }}
            onSettings={() => navigate('/settings')}
          />
          {/* Mobile-only hamburger. The desktop nav above is hidden under
              md, so without this students on phones couldn't navigate
              between Dashboard / Messages / Workspace. */}
          <MobileMenu showWorkspace={showWorkspace} showOrgAdmin={showOrgAdmin} />
        </div>
      </div>
    </header>
  );
}

/**
 * Mobile-only header menu — hamburger that drops the nav links into a
 * panel. Hidden at `md:` and up where the inline `<nav>` is visible.
 * Click-outside + ESC close the panel.
 */
function MobileMenu({
  showWorkspace,
  showOrgAdmin,
}: {
  showWorkspace: boolean;
  showOrgAdmin: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative md:hidden" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card transition-colors hover:bg-secondary"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {open ? <X className="h-4 w-4" aria-hidden /> : <Menu className="h-4 w-4" aria-hidden />}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 origin-top-right animate-fade-up overflow-hidden rounded-xl border border-border bg-card shadow-lg"
        >
          <MobileMenuLink to="/dashboard" onSelect={() => setOpen(false)}>
            Dashboard
          </MobileMenuLink>
          <MobileMenuLink to="/messages" onSelect={() => setOpen(false)}>
            Messages
          </MobileMenuLink>
          <MobileMenuLink to="/tutor" onSelect={() => setOpen(false)}>
            Tutor
          </MobileMenuLink>
          {showWorkspace && (
            <MobileMenuLink to="/workspace" onSelect={() => setOpen(false)}>
              Workspace
            </MobileMenuLink>
          )}
          {showOrgAdmin && (
            <MobileMenuLink to="/org-admin" onSelect={() => setOpen(false)}>
              Org Admin
            </MobileMenuLink>
          )}
        </div>
      )}
    </div>
  );
}

function MobileMenuLink({
  to,
  onSelect,
  children,
}: {
  to: string;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      onClick={onSelect}
      className={({ isActive }) =>
        cn(
          'block px-4 py-2.5 text-sm font-medium transition-colors',
          isActive ? 'bg-secondary text-foreground' : 'text-foreground hover:bg-secondary/60',
        )
      }
    >
      {children}
    </NavLink>
  );
}

function HeaderLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-secondary text-foreground'
            : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
        )
      }
    >
      {children}
    </NavLink>
  );
}

function UserMenu({
  name,
  activeRole,
  initials,
  onSignOut,
  onSettings,
}: {
  name: string;
  activeRole: string | null;
  initials: string;
  onSignOut: () => void;
  onSettings: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg border border-border bg-card px-2 py-1.5 text-sm transition-colors hover:bg-secondary"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
          aria-hidden
        >
          {initials || '?'}
        </span>
        <span className="hidden font-medium sm:block">{name}</span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 origin-top-right animate-fade-up overflow-hidden rounded-xl border border-border bg-card shadow-lg"
        >
          <div className="border-b border-border px-4 py-3">
            <div className="text-sm font-semibold">{name}</div>
            {activeRole && (
              <div className="mt-0.5 text-xs uppercase tracking-wider text-muted-foreground">
                {activeRole.replace(/_/g, ' ')}
              </div>
            )}
          </div>
          <MenuItem icon={User} onClick={onSettings} label="Profile & settings" />
          <MenuItem icon={Settings} onClick={onSettings} label="Preferences" />
          <div className="border-t border-border" />
          <MenuItem icon={LogOut} onClick={onSignOut} label="Sign out" tone="destructive" />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  tone?: 'destructive';
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm transition-colors',
        tone === 'destructive' ? 'text-destructive hover:bg-destructive/10' : 'hover:bg-secondary',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {label}
    </button>
  );
}
